import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import { Markdown } from "tiptap-markdown";
import { marked } from "marked";

import "./CourseNoteRichEditor.css";

// Configure marked for safer parsing
marked.setOptions({
  breaks: true,
  gfm: true,
});

/**
 * Detect if a string looks like HTML (to avoid double-parsing)
 */
const looksLikeHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

/**
 * Convert markdown to HTML, with guard against double-parsing
 */
const mdToHtml = (md) => {
  if (!md) return "";
  // If somehow we got HTML, DO NOT marked.parse it (it will wrap/escape weirdly)
  if (looksLikeHtml(md)) {
    console.warn('[CourseNoteRichEditor] Received HTML instead of markdown, returning as-is');
    return md;
  }
  return marked.parse(md);
};

/**
 * CourseNoteRichEditor Component
 * WYSIWYG rich text editor using TipTap that serializes to/from Markdown
 * 
 * TipTap maintains its own internal state. Parent doesn't sync on every keystroke.
 * Parent calls editor.storage.markdown.getMarkdown() when saving.
 * 
 * @param {Object} props
 * @param {string} props.noteId - Note ID to track when switching notes
 * @param {string} props.initialMd - Initial markdown content (only loaded on noteId change)
 * @param {Function} props.onReady - Callback when editor is ready, receives editor instance
 * @param {string} props.placeholder - Placeholder text for empty editor
 * @param {boolean} props.readOnly - Read-only mode
 * @param {Function} props.onBlur - Blur event handler
 */
export default function CourseNoteRichEditor({
  noteId,
  initialMd,
  onReady,
  placeholder = "Start taking notes here…",
  readOnly = false,
  onBlur,
  dictation,
}) {
  const loadedNoteIdRef = useRef(null);
  const lastLoadedMdRef = useRef("");

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Highlight.configure({ multicolor: false }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
      }),
      Placeholder.configure({ placeholder }),
      Markdown.configure({
        html: true, // Allow HTML tags for marks like highlight/underline
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: "", // start empty, will load on noteId change
    onBlur: () => {
      if (onBlur) {
        onBlur();
      }
    },
  });

  // Notify parent when editor instance is ready
  useEffect(() => {
    if (!editor) return;
    console.log('[CourseNoteRichEditor] Editor ready');
    onReady?.(editor);
  }, [editor, onReady]);

  // ONLY load content when switching to a different noteId OR when initialMd changes
  useEffect(() => {
    if (!editor) return;
    if (!noteId) return;

    const noteChanged = loadedNoteIdRef.current !== noteId;
    const contentChanged = initialMd && initialMd !== lastLoadedMdRef.current;
    
    if (!noteChanged && !contentChanged) {
      return; // Same note, same content - skip
    }

    console.log('[CourseNoteRichEditor] Loading note:', noteId, 'reason:', noteChanged ? 'note changed' : 'content updated');
    loadedNoteIdRef.current = noteId;
    lastLoadedMdRef.current = initialMd || "";
    
    const content = initialMd || "";
    
    // If content looks like HTML, parse it first, otherwise use as markdown
    if (looksLikeHtml(content)) {
      console.log('[CourseNoteRichEditor] Content is HTML, converting to display');
      const html = mdToHtml(content); // This will pass through HTML as-is
      editor.commands.setContent(html, false);
    } else {
      console.log('[CourseNoteRichEditor] Content is markdown, loading directly');
      editor.commands.setContent(content, false);
    }
  }, [noteId, editor, initialMd]); // <- depends on noteId AND initialMd

  const can = editor?.can();

  return (
    <div className="dhRichNote">
      <div className="dhRichToolbar dhNotesToolbar">
        {/* Text Group: Bold, Italic, H1, H2, H3 */}
        <div className="toolbar-group">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleBold().run();
            }}
            disabled={readOnly || !can?.chain().focus().toggleBold().run()}
            className={editor?.isActive("bold") ? "isActive" : ""}
            title="Bold (Ctrl+B)"
          >
            <strong>B</strong>
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleItalic().run();
            }}
            disabled={readOnly || !can?.chain().focus().toggleItalic().run()}
            className={editor?.isActive("italic") ? "isActive" : ""}
            title="Italic (Ctrl+I)"
          >
            <em>I</em>
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleHeading({ level: 1 }).run();
            }}
            disabled={readOnly}
            className={editor?.isActive("heading", { level: 1 }) ? "isActive" : ""}
            title="Heading 1"
          >
            H1
          </button>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleHeading({ level: 2 }).run();
            }}
            disabled={readOnly}
            className={editor?.isActive("heading", { level: 2 }) ? "isActive" : ""}
            title="Heading 2"
          >
            H2
          </button>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleHeading({ level: 3 }).run();
            }}
            disabled={readOnly}
            className={editor?.isActive("heading", { level: 3 }) ? "isActive" : ""}
            title="Heading 3"
          >
            H3
          </button>
        </div>

        {/* List Group: Bullet, Numbered */}
        <div className="toolbar-group">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleBulletList().run();
            }}
            disabled={readOnly}
            className={editor?.isActive("bulletList") ? "isActive" : ""}
            title="Bullet List"
          >
            • List
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleOrderedList().run();
            }}
            disabled={readOnly}
            className={editor?.isActive("orderedList") ? "isActive" : ""}
            title="Numbered List"
          >
            1. List
          </button>
        </div>

        {/* Insert Group: Link, Code, Quote */}
        <div className="toolbar-group">
          <button
            type="button"
            disabled={readOnly}
            onMouseDown={(e) => {
              e.preventDefault();
              const prev = editor.getAttributes("link").href || "";
              const href = window.prompt("Link URL", prev);
              if (href === null) return;
              if (href.trim() === "") {
                editor.chain().focus().unsetLink().run();
                return;
              }
              editor.chain().focus().setLink({ href: href.trim() }).run();
            }}
            className={editor?.isActive("link") ? "isActive" : ""}
            title="Add Link"
          >
            &#128279;
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleCodeBlock().run();
            }}
            disabled={readOnly}
            className={editor?.isActive("codeBlock") ? "isActive" : ""}
            title="Code Block"
          >
            {"</>"}
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleBlockquote().run();
            }}
            disabled={readOnly}
            className={editor?.isActive("blockquote") ? "isActive" : ""}
            title="Quote"
          >
            &ldquo; &rdquo;
          </button>
        </div>

        {/* Format Group: Underline, Highlight */}
        <div className="toolbar-group">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleUnderline().run();
            }}
            disabled={readOnly}
            className={editor?.isActive("underline") ? "isActive" : ""}
            title="Underline (Ctrl+U)"
          >
            <u>U</u>
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().toggleHighlight().run();
            }}
            disabled={readOnly}
            className={editor?.isActive("highlight") ? "isActive" : ""}
            title="Highlight"
          >
            &#9998;&#65039;
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().setHorizontalRule().run();
            }}
            disabled={readOnly}
            title="Horizontal Divider"
          >
            —
          </button>
        </div>

        {/* Undo/Redo Group */}
        <div className="toolbar-group">
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().undo().run();
            }}
            disabled={readOnly || !editor?.can().undo()}
            title="Undo (Ctrl+Z)"
          >
            &#8694;
          </button>

          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              editor?.chain().focus().redo().run();
            }}
            disabled={readOnly || !editor?.can().redo()}
            title="Redo (Ctrl+Shift+Z)"
          >
            &#8695;
          </button>
        </div>

        {/* Dictation */}
        {dictation?.isSupported && (
          <div className="toolbar-group">
            <button
              type="button"
              className={`toolbar-dictate${dictation.status === 'recording' ? ' recording' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                dictation.status === 'recording' ? dictation.stop() : dictation.start();
              }}
              disabled={readOnly || dictation.status === 'processing'}
              title={dictation.status === 'recording' ? 'Stop dictation' : 'Dictate notes'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
                <line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className={`dhRichCanvas ${readOnly ? "isReadOnly" : ""}`}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
